<template>
  <div class="owc-stage__pricing wb-price wb-price--medium">
    <div v-if="price" class="wb-price__content">
      <p class="wb-price__headline" v-html="price.headline"></p>
      <p class="wb-price__value">
        <span class="wb-price__starting" v-if="price.pricePrefix" v-html="price.pricePrefix"></span>
        <span class="wb-price__price" v-html="price.priceWithCurrency"></span>
        <wb-tooltip-host v-if="price.tooltip.text" :position="price.tooltip.position">
          <wb-icon :name="price.tooltip.icon"></wb-icon>
          <wb-tooltip hidden>
            <div v-html="price.tooltip.text"></div>
          </wb-tooltip>
        </wb-tooltip-host>
      </p>
      <p class="wb-price__headline" v-html="price.priceHint"></p>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { getStore } from '@/store';
import { StagePriceComputed, StagePriceData, StagePriceMethods, StagePriceProps } from './StagePrice';
import { CtaButtonsDisplayMethodPrice } from '@owc-utils/AutomatedCta/CtaAutomation.enum';
import { isShimLoaded } from '@owc-utils/Shims/moduleShim';

export default Vue.extend<StagePriceData, StagePriceMethods, StagePriceComputed, StagePriceProps>({
  name: 'owc-stage-price',
  props: {
    priceData: {
      type: Object,
      required: true,
    },
    vehicleData: {
      type: Object,
      required: false,
    },
  },
  computed: {
    isAutomaticStartingPrice() {
      return this.priceData.startingPriceMethod === CtaButtonsDisplayMethodPrice.Automate;
    },
  },
  data() {
    return {
      market: '',
      language: '',
      price: null,
    };
  },
  async mounted() {
    await this.setPrice();
  },
  methods: {
    async setPrice() {
      // VCA does not work in ios <16 so let's call this hack
      // a "graceful degradation" while VCA is not updated
      if (this.isAutomaticStartingPrice && !isShimLoaded()) {
        const { isWithinVehicleContext } = await import('@dh-io-globalelem/vehicle-data-store-connection');
        if ((await isWithinVehicleContext()) && !this.vehicleData?.scoped) {
          this.setPriceFromStore();
        } else {
          await this.setPriceFromService();
        }
      } else {
        this.setPriceFromProps();
      }
    },
    async setPriceFromStore() {
      const store = await getStore();
      const { connectionName } = await import('@dh-io-globalelem/vehicle-data-store-connection');
      store.subscribe(connectionName, state => {
        this.$nextTick(() => {
          this.price = this.mapToPriceInformations(state.price);
        });
      });
    },
    async setPriceFromService() {
      if (this.vehicleData?.modelSeries) {
        const { modelSeries, subBrands, engineConcept } = this.vehicleData;
        const { loadPriceData } = await import('@dh-io-globalelem/vehicle-context-automation');
        const configuration = await loadPriceData(modelSeries, engineConcept, subBrands);
        if (configuration) {
          this.price = this.mapToPriceInformations(configuration);
        }
      }
    },
    setPriceFromProps() {
      this.price = this.priceData;
    },
    mapToPriceInformations(priceData) {
      return {
        ...this.priceData,
        priceWithCurrency: priceData.min,
        pricePrefix: priceData.pricePrefixLabel,
        tooltip: {
          ...this.priceData.tooltip,
          text: priceData?.priceFootnoteLabel || '',
        },
      };
    },
  },
});
</script>

<style lang="scss">
.owc-stage__pricing {
  margin-top: var(--wb-spacing-xxs);

  text-align: center;

  @include breakpoint-from(mq3) {
    margin-top: var(--wb-spacing-s);

    text-align: left;
  }

  .wb-price__content .wb-price__value {
    display: inline-flex;
  }
}
</style>
